import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';
import { leadKeys } from '@/features/leads/useLeads';
import { inventoryKeys } from '@/features/inventory/useInventory';

export const dealKeys = {
  all: ['deals'] as const,
  forLead: (leadId: string) => ['deals', 'lead', leadId] as const,
  commissions: (filters: CommissionFilters) => ['deals', 'commissions', filters] as const,
};

export type DealStatus = 'reserved' | 'contracted' | 'cancelled' | 'refunded';
export type CommissionStatus = 'expected' | 'due' | 'invoiced' | 'received' | 'paid';

export const COMMISSION_STATUSES: readonly CommissionStatus[] = [
  'expected',
  'due',
  'invoiced',
  'received',
  'paid',
] as const;

export interface Deal {
  id: string;
  lead_id: string;
  unit_id: string;
  sales_id: string;
  type: 'reservation' | 'contract';
  reservation_date: string;
  contract_date: string | null;
  unit_value: string;
  status: DealStatus;
  cancellation_reason: string | null;
  units: { unit_code: string; projects: { name: string } | null } | null;
}

export interface Commission {
  id: string;
  deal_id: string;
  sales_id: string;
  unit_value: string;
  commission_percent: string;
  company_percent: string;
  agent_percent: string;
  team_leader_percent: string;
  gross_commission: string;
  company_share: string;
  agent_share: string;
  team_leader_share: string;
  status: CommissionStatus;
  due_date: string | null;
  received_date: string | null;
  paid_date: string | null;
  voided_at: string | null;
  voided_reason: string | null;
  deals: {
    reservation_date: string;
    contract_date: string | null;
    status: DealStatus;
    units: { unit_code: string } | null;
    leads: { full_name: string } | null;
  } | null;
  profiles: { full_name: string } | null;
}

// prettier-ignore
const DEAL_COLUMNS = 'id, lead_id, unit_id, sales_id, type, reservation_date, contract_date, unit_value, status, cancellation_reason, units(unit_code, projects(name))';
// commissions has four foreign keys to profiles (sales, team leader, created_by,
// updated_by), so a bare `profiles(...)` embed is ambiguous and PostgREST refuses
// it with PGRST201. The relationship has to be named.
// prettier-ignore
const COMMISSION_COLUMNS = 'id, deal_id, sales_id, unit_value, commission_percent, company_percent, agent_percent, team_leader_percent, gross_commission, company_share, agent_share, team_leader_share, status, due_date, received_date, paid_date, voided_at, voided_reason, deals(reservation_date, contract_date, status, units(unit_code), leads(full_name)), profiles!commissions_sales_id_fkey(full_name)';

export function useLeadDeals(leadId: string | undefined) {
  return useQuery({
    queryKey: leadId ? dealKeys.forLead(leadId) : ['deals', 'lead', 'none'],
    enabled: Boolean(leadId),
    queryFn: async (): Promise<Deal[]> => {
      const { data, error } = await supabase
        .from('deals')
        .select(DEAL_COLUMNS)
        .eq('lead_id', leadId!)
        .order('reservation_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Deal[];
    },
  });
}

export interface CommissionFilters {
  status?: string;
  salesId?: string;
  includeVoided?: boolean;
}

export function useCommissions(filters: CommissionFilters) {
  return useQuery({
    queryKey: dealKeys.commissions(filters),
    queryFn: async (): Promise<Commission[]> => {
      let query = supabase.from('commissions').select(COMMISSION_COLUMNS);

      if (filters.status) query = query.eq('status', filters.status);
      if (filters.salesId) query = query.eq('sales_id', filters.salesId);
      // Voided commissions are hidden by default but never deleted — finance has
      // to be able to see what was voided and why, so this is a filter, not a
      // permanent exclusion.
      if (!filters.includeVoided) query = query.is('voided_at', null);

      const { data, error } = await query.order('created_at', { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as Commission[];
    },
  });
}

/**
 * Everything that changes a deal invalidates leads and inventory too: a
 * reservation moves the lead's stage and takes a unit off the market, and a
 * cancellation puts it back. Invalidating only `deals` would leave the lead page
 * and the unit browser showing a world that no longer exists.
 */
function useDealMutation<TVars>(fn: (vars: TVars) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dealKeys.all });
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
      void queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

export function useCreateReservation() {
  return useDealMutation(
    (vars: { leadId: string; unitId: string; holdDays?: number; notes?: string | undefined }) =>
      rpc<Deal>('create_reservation', {
        p_lead_id: vars.leadId,
        p_unit_id: vars.unitId,
        p_hold_days: vars.holdDays ?? 14,
        p_notes: vars.notes ?? null,
      }),
  );
}

export function useConvertToContract() {
  return useDealMutation(
    (vars: { dealId: string; contractDate?: string | undefined; notes?: string | undefined }) =>
      rpc<Deal>('convert_to_contract', {
        p_deal_id: vars.dealId,
        p_contract_date: vars.contractDate ?? null,
        p_notes: vars.notes ?? null,
      }),
  );
}

export function useCancelDeal() {
  return useDealMutation((vars: { dealId: string; reason: string; refund?: boolean }) =>
    rpc<Deal>('cancel_deal', {
      p_deal_id: vars.dealId,
      p_reason: vars.reason,
      p_refund: vars.refund ?? false,
    }),
  );
}

export function useSetCommissionStatus() {
  return useDealMutation(
    (vars: { commissionId: string; status: CommissionStatus; date?: string; notes?: string }) =>
      rpc<Commission>('set_commission_status', {
        p_commission_id: vars.commissionId,
        p_status: vars.status,
        p_date: vars.date ?? null,
        p_notes: vars.notes ?? null,
      }),
  );
}
